import { getHubUrl, getToken } from './config';

export type SSEEvent =
  | { type: 'question_new'; data: { question: import('./types').Question } }
  | { type: 'question_answered'; data: { question_id: string; answer: import('./types').QuestionAnswer } }
  | { type: 'question_defaulted'; data: { question_id: string } }
  | { type: 'agent_state'; data: { state: import('./types').AgentState } }
  | { type: 'permission_new'; data: { request: import('./types').PermissionRequest } }
  | { type: 'connected'; data: { lastEventId?: string } }
  | { type: 'disconnected'; data: { reason: string } };

export type SSEHandler = (ev: SSEEvent) => void;

export function openInboxStream(onEvent: SSEHandler): () => void {
  let es: EventSource | null = null;
  let closed = false;
  let attempt = 0;
  let reconnectTimer: number | undefined;

  const connect = () => {
    if (closed) return;
    const token = getToken();
    if (!token) return;
    const url = `${getHubUrl()}/v1/stream/inbox?token=${encodeURIComponent(token)}`;
    try {
      es = new EventSource(url, { withCredentials: false });
    } catch (e) {
      onEvent({ type: 'disconnected', data: { reason: String(e) } });
      scheduleReconnect();
      return;
    }

    es.addEventListener('open', () => {
      attempt = 0;
      onEvent({ type: 'connected', data: {} });
    });

    const wire = (name: SSEEvent['type']) => {
      es?.addEventListener(name, (e: MessageEvent) => {
        try {
          const data = e.data ? JSON.parse(e.data) : {};
          onEvent({ type: name, data } as SSEEvent);
        } catch {
          // ignore malformed
        }
      });
    };
    wire('question_new');
    wire('question_answered');
    wire('question_defaulted');
    wire('agent_state');
    wire('permission_new');

    es.onerror = () => {
      onEvent({ type: 'disconnected', data: { reason: 'error' } });
      es?.close();
      es = null;
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closed) return;
    attempt += 1;
    const delay = Math.min(30000, 500 * 2 ** Math.min(attempt, 6));
    reconnectTimer = window.setTimeout(connect, delay);
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    es?.close();
  };
}
